import { keys as lodashKeys, get, set, each, isObject, concat, find, reduce, map, some, isEmpty, chain } from "lodash"
import { computeRemoveFilter } from "./filters"
import { ATTRIBUTION_METHODS } from "./risk"

export const FILTERS = {
  LONG_SHORT: "longShort",
  COUNTRY: "country",
  CURRENCY: "currency",
  ASSET_CLASS: "assetClass",
  ASSET_SUBCLASS: "assetSubclass",
  AVG_DAILY_VOLUME: "averageDailyVolume",
  MARKET_CAPITALIZATION: "marketCapitalization",
}

export const NORMALIZATION = {
  CONTRIBUTION: "CONTRIBUTION",
  STANDALONE: "STANDALONE",
}

export function segmentPositionSet(positionSet, segment) {
  if (!segment) { return positionSet }
  return {
    segment,
    ...positionSet,
  }
}

export function getSegmentLabel(segment, item) {
  if (!segment && get(item, "label")) { return item.label }
  const keys = getAllKeys(segment)
  let label

  const key = keys.find((key) => key.match(/longShort/gi))
  if (key) {
    label = get(segment, key)
  }
  // Add more cases for different naming strategies
  // key = keys.find((key) => key.match(/currency/))

  if (!item) { return label }

  if (get(item, "label", "").indexOf(label) !== -1) {
    return item.label
  }

  return `${item.label} - ${label}`
}

export function getSegmentNormalization(item) {
  return get(item, "positionSet.segment.normalization") || get(item, "segment.normalization") || get(item, "normalization")
}

export function changeSegmentNormalization(item, normalization) {
  return set(item, "segment.normalization", normalization)
}

export function isSegmentUnmodeled(segment, experiment, isRefUnmodeled) {
  const isShort = get(experiment, "config.setup.tradeSize", 0) < 0
  const keys = getAllKeys(segment)
  const longShort = find(keys, (key) => key.match(/longShort/gi))
  if (longShort && get(segment, longShort) === (isShort ? "SHORT" : "LONG") && isRefUnmodeled) {
    return true
  }
  return false
}

function getAllKeys(item, prevKey) {
  let keys = []
  each(lodashKeys(item), (key) => {
    if (isObject(item[key])) {
      keys = concat(keys, getAllKeys(item[key], prevKey ? `${prevKey}.${key}` : key))
    } else {
      keys.push(prevKey ? `${prevKey}.${key}` : key)
    }
  })
  return keys
}

export function computeFilteredSegment(filter, normalization) {
  if (!filter) {
    return null
  }

  let updatedFilter = filter

  // Convert securities to segment filter format
  if (filter.securities) {
    updatedFilter = {
      ...filter,
      securities: reduce(filter.securities, (securitiesFilter, securityIds, operator) => {
        securitiesFilter[operator] = map(securityIds, securityId => ({
          id: getFilterSecurityId(securityId),
        }))
        return securitiesFilter
      }, {}),
    }
  }

  return {
    filters: updatedFilter,
    normalization,
  }
}

function getFilterSecurityId(security) {
  if (get(security, "assetClass") === "index") {
    return { indexId: security.indexId || security.ticker }
  }

  return chain(security).pick(["ticker", "mic", "country", "internalId"]).omitBy(v => !v).value()
}

/**
 * Returns true if the given filter is supported in active mode with normalization set to "Contribution".
 */
export const filterSupportsContribution = (filter, isActive) => {
  if (!filter || !isActive) { return true }
  return !some(["tag", "positionAttributes"], filterType => get(filter, filterType))
}

/**
 * Removes filters that are not supported, based on active mode and normalization. Does not mutate the original filter.
 */
export const removeUnsupportedFilters = (filter, isActive, normalization) => {
  if (!filter || !isActive || normalization === NORMALIZATION.STANDALONE) {
    return filter
  }

  return reduce(["tag", "positionAttributes"], (currentFilter, filterType) => {
    if (get(currentFilter, filterType)) {
      return computeRemoveFilter(currentFilter, filterType, [])
    }
    return currentFilter
  }, filter)
}

export const removeUnsupportedAddedFilters = (addedFilters, isActive, normalization) => {
  if (!addedFilters || !isActive || normalization === NORMALIZATION.STANDALONE) {
    return addedFilters
  }

  return addedFilters.filter(filter => !["tag", "positionAttributes"].includes(filter.filterOn.type))
}

export function computeMCTEAdjustedSegment(segment, attributionMethod, isActive) {
  if (isEmpty(segment)) { return segment }
  if (isActive && attributionMethod === ATTRIBUTION_METHODS.MCTE) {
    return {
      ...segment,
      normalization: NORMALIZATION.STANDALONE,
    }
  }
  return segment
}

export function computeMCTEAdjustedBase(base, attributionMethod, isActive) {
  if (isActive && attributionMethod === ATTRIBUTION_METHODS.MCTE && get(base, "segment")) {
    return set({ ...base }, "segment.normalization", NORMALIZATION.STANDALONE)
  }
  return base
}
